import React, { useState } from "react";
import {
  Drawer,
  TextField,
  Button,
  Box,
  Typography,
  Divider,
  useTheme,
  IconButton,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import {
  EmailOutlined,
  PersonOutline,
  PhoneOutlined,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { createMember } from "../../services/MembersService";
import { validatePhoneNumber } from "../../utils";
import { capitalizeFirstLetter, isValidEmail } from "../../utils/commons";
interface AddUserDrawerProps {
  open: boolean;
  onClose: () => void;
  setCurrentRole: React.Dispatch<React.SetStateAction<any>>;
  fetchMembers: (page: number) => Promise<void>;
}

interface UserData {
  fname: string; // *
  lname: string; // *
  phone: string; // *
  email?: string; // ?
  gender: string; // *
}

const AddUserDrawer: React.FC<AddUserDrawerProps> = ({
  open,
  onClose,
  setCurrentRole,
  fetchMembers,
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [userData, setUserData] = useState<UserData>({
    fname: "",
    lname: "",
    phone: "",
    email: "",
    gender: "MALE",
  });

  const inputStyle = {
    marginTop: theme.spacing(1),
    "& .MuiInputBase-input": {
      color: theme.palette.text.primary,
      fontSize: "1rem",
    },
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setUserData((prevUserData) => ({
      ...prevUserData,
      [name]: value,
    }));
  };

  const handleSaveUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    if (
      !userData.fname ||
      !userData.lname ||
      !userData.phone ||
      !userData.gender
    ) {
      enqueueSnackbar("Required Fileds Missing");
      setSubmitting(false);
      return;
    }

    if (!validatePhoneNumber(userData.phone)) {
      enqueueSnackbar("Invalid PhoneNo");
      setSubmitting(false);
      return;
    }

    if (userData.email && !isValidEmail(userData.email)) {
      enqueueSnackbar("Invalid Email");
      setSubmitting(false);
      return;
    }
    const memberData = {
      firstName: capitalizeFirstLetter(userData.fname),
      lastName: capitalizeFirstLetter(userData.lname),
      email: userData.email,
      phone: userData.phone,
      gender: userData.gender,
    };

    const result = await createMember(memberData);

    if (result.status == 201) {
      enqueueSnackbar("Member created successfully");
      onClose();
      setCurrentRole("ALL");
      fetchMembers(1);

      setSubmitting(false);
      setUserData({
        fname: "",
        lname: "",
        phone: "",
        email: "",
        gender: "MALE",
      });
      return;
    }

    if (result.response.data?.statusCode == 409) {
      enqueueSnackbar("User already exists");
      setSubmitting(false);
      return;
    }
  };

  const handleResetForm = () => {
    setUserData({
      fname: "",
      lname: "",
      phone: "",
      email: "",
      gender: "MALE",
    });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          backgroundColor: theme.palette.background.paper,
          borderTopLeftRadius: theme.spacing(3),
          borderBottomLeftRadius: theme.spacing(3),
          width: 350,
          boxSizing: "border-box",
        },
      }}
    >
      <Box
        component="form"
        onSubmit={handleSaveUser}
        aria-label="User Form"
        sx={{
          backgroundColor: theme.palette.background.paper,
          height: "100vh",
          padding: theme.spacing(3),
          boxSizing: "border-box",
        }}
      >
        <Box
          mb={2}
          mt={2}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              marginRight: theme.spacing(1),
            }}
          >
            <KeyboardBackspaceIcon />
          </IconButton>
          <Typography variant="h6" gutterBottom>
            Add New Member
          </Typography>
        </Box>

        <Divider sx={{ marginBottom: theme.spacing(2) }} />
        <Box sx={{ marginBottom: theme.spacing(2) }}>
          <label htmlFor="fname">
            First Name{" "}
            <span style={{ color: theme.palette.text.secondary }}>*</span>
          </label>
          <TextField
            id="fname"
            variant="outlined"
            name="fname"
            value={userData.fname}
            onChange={handleInputChange}
            sx={inputStyle}
            required
            fullWidth
            InputProps={{
              startAdornment: (
                <PersonOutline
                  style={{
                    color: theme.palette.text.secondary,
                    marginRight: theme.spacing(1),
                  }}
                />
              ),
            }}
          />
        </Box>
        <Box sx={{ marginBottom: theme.spacing(2) }}>
          <label htmlFor="lname">
            Last Name{" "}
            <span style={{ color: theme.palette.text.secondary }}>*</span>
          </label>
          <TextField
            id="lname"
            variant="outlined"
            name="lname"
            value={userData.lname}
            onChange={handleInputChange}
            sx={inputStyle}
            required
            fullWidth
            InputProps={{
              startAdornment: (
                <PersonOutline
                  style={{
                    color: theme.palette.text.secondary,
                    marginRight: theme.spacing(1),
                  }}
                />
              ),
            }}
          />
        </Box>
        <Box sx={{ marginBottom: theme.spacing(2) }}>
          <label htmlFor="phone">
            Phone <span style={{ color: theme.palette.text.secondary }}>*</span>
          </label>

          <TextField
            id="phone"
            variant="outlined"
            name="phone"
            value={userData.phone}
            onChange={handleInputChange}
            sx={inputStyle}
            required
            fullWidth
            inputProps={{
              inputMode: "numeric",
              maxLength: 10,
            }}
            InputProps={{
              startAdornment: (
                <PhoneOutlined
                  style={{
                    color: theme.palette.text.secondary,
                    marginRight: theme.spacing(1),
                  }}
                />
              ),
            }}
          />
        </Box>
        <Box sx={{ marginBottom: theme.spacing(2) }}>
          <label htmlFor="email">Email</label>
          <TextField
            id="email"
            variant="outlined"
            name="email"
            value={userData.email}
            onChange={handleInputChange}
            sx={inputStyle}
            fullWidth
            InputProps={{
              startAdornment: (
                <EmailOutlined
                  style={{
                    color: theme.palette.text.secondary,
                    marginRight: theme.spacing(1),
                  }}
                />
              ),
            }}
          />
        </Box>
        <Box sx={{ marginBottom: theme.spacing(2) }}>
          <FormControl component="fieldset">
            <FormLabel
              component="legend"
              sx={{ color: theme.palette.text.primary }}
            >
              Gender{" "}
              <span style={{ color: theme.palette.text.secondary }}>*</span>
            </FormLabel>
            <RadioGroup
              aria-label="gender"
              name="gender"
              value={userData.gender}
              onChange={handleInputChange}
              row
            >
              <FormControlLabel value="MALE" control={<Radio />} label="Male" />
              <FormControlLabel
                value="FEMALE"
                control={<Radio />}
                label="Female"
              />
              <FormControlLabel
                value="OTHER"
                control={<Radio />}
                label="Other"
              />
            </RadioGroup>
          </FormControl>
        </Box>
        <Box sx={{ marginTop: theme.spacing(4), textAlign: "center" }}>
          <Button
            disabled={submitting ? true : false}
            type="submit"
            variant="outlined"
            color="primary"
            sx={{
              fontSize: "1rem",
              borderRadius: theme.spacing(1),
            }}
          >
            Add Member
          </Button>
          <Button
            type="button"
            variant="outlined"
            color="secondary"
            onClick={handleResetForm}
            sx={{
              fontSize: "1rem",
              borderRadius: theme.spacing(1),
              marginLeft: theme.spacing(2),
            }}
          >
            Clear
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default AddUserDrawer;
